import React, { useRef, useState, useEffect } from "react"
import styled from "styled-components"


const Journey = () => {

  const stepOneRef = useRef();
  const stepTwoRef = useRef();
  const stepThreeRef = useRef();
  const stepFourRef = useRef();
  const [activeStep,setActiveStep] = useState(1);

  function handleIntersect(entries, observer) {
    let el = entries[0].target;
    ( entries[0].intersectionRatio > 0.5 ) && setActiveStep(el.getAttribute('data-step'));
  }

  function setupObserver(el) {
    let observer;
    let options = {
      root: null,
      rootMargin: "0px",
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };
    observer = new IntersectionObserver(handleIntersect, options);
    observer.observe(el);
  }
  useEffect(() => {
    stepOneRef && setupObserver(stepOneRef.current);
    stepTwoRef && setupObserver(stepTwoRef.current);
    stepThreeRef && setupObserver(stepThreeRef.current);
    stepFourRef && setupObserver(stepFourRef.current);
  }, []);

  return (
      <StyledJourney className={`step-${activeStep}`}>
        <figure style={{backgroundImage: "url(/investigations/the-secretive-libyan-prisons-that-keep-migrants-out-of-europe/images/journey-1-three.svg)"}}></figure>
        <div>
          <div>
            <p className="has-drop-cap" ref={stepOneRef} data-step="1">Around 10 <small>p.m.</small> on February 3, 2021, a smuggler led Candé and a hundred and thirty others to the Libyan coast, and launched them from shore in an inflatable rubber dinghy. Some of the migrants, excited by the departure, broke into song. Roughly two hours later, the boat entered international waters. Candé, straddling the side of the dinghy, felt hopeful. He told others on board that he was thinking about bringing his wife and children to join him.</p>

            <p ref={stepTwoRef} data-step="2">The trafficker had put three migrants in charge. A “bussolier” guided the dinghy along its route using a compass. A “captain” manned the motor and handled the satellite phone; once they were far enough from Libya, he was supposed to call Alarm Phone, a migration activist group, and request a rescue. A “commander” kept order and made sure no one touched the plug that, if pulled, would deflate the vessel. Soon, the seas grew rough, making nearly everyone sick and turning the pooling water at their feet into a soup of vomit, feces, candy wrappers, and baguette crumbs. Several migrants tried to bail out the boat using plastic water bottles cut in half. A fight erupted, and someone threatened to slash the dinghy with a knife before he was subdued. Mohamed David Soumahoro, who befriended Candé on the boat, recalled, “Everyone started calling out for their God—one for Allah, the other calling Jesus, another calls this one and another that one. Women began crying, and once they saw people panicking the babies began crying, too.”</p> 
          </div>
        </div>

        <figure style={{backgroundImage: "url(/investigations/the-secretive-libyan-prisons-that-keep-migrants-out-of-europe/images/journey-2-three.svg)"}}></figure>

        <div ref={stepThreeRef} data-step="3">
          <div>
            <p>At dawn, the waters calmed, and the migrants, deciding that they were far enough from Libya, called Alarm Phone for help. A volunteer told them that there was a merchant vessel not far away. This sparked jubilation. “<em>Bosa</em>, free, <em>bosa</em>, free,” the migrants chanted, using a celebratory Fula expression. Candé turned to Soumahoro, his eyes lighting up, and said, “<em>Inshallah</em>, we’re going to make it! Italy!” But when the merchant vessel arrived the captain announced that he had no lifeboats and quickly steered away.</p>

            <p>By now, Candé’s boat was seventy miles from Tripoli, out of Libyan waters but still within the Coast Guard’s expanded jurisdiction. Around 5 <small>p.m.</small> on February 4th, the migrants noticed an airplane overhead, which circled for fifteen minutes, then flew away. Data from the ADS-B Exchange, an organization that tracks aviation traffic, show that the plane was the Eagle1, a white Beech King Air 350 surveillance aircraft leased by Frontex. (The agency declined to comment on its role in the capture.) About three hours later, a boat appeared on the horizon. “The closer it came, the clearer we saw it—and saw the black and green lines of the flag,” Soumahoro told me. “Every one started crying and holding their heads, saying, ‘Shit, it’s Libyan.’”</p>
          </div>
        </div>

        <figure style={{backgroundImage: "url(/investigations/the-secretive-libyan-prisons-that-keep-migrants-out-of-europe/images/journey-3-three.svg)"}}></figure>
        <div ref={stepFourRef} data-step="4">
          <div>
            <p>The boat, a Vittoria P350 made of steel, fibreglass, and Kevlar, was one of the cutters unveiled by the E.U. It rammed the dinghy three times, then Coast Guard officers ordered the migrants to climb aboard. “Move!” they yelled. One hit several of the migrants with the butt of his rifle; another whipped them with a rope. The migrants were taken back to land, loaded into buses and trucks, and driven to Al Mabani.</p>
          </div>
        </div>
        <figure style={{backgroundImage: "url(/investigations/the-secretive-libyan-prisons-that-keep-migrants-out-of-europe/images/journey-4-three.svg)"}}></figure>

      </StyledJourney>
  )
}

export default Journey;


const StyledJourney = styled.div`
  position: relative;
  z-index: 102;
  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 50%;
    margin-left: -50vw;
    width: 100vw;
    height: 3px;
    background-color: var(--theme-background-dark);
    opacity: 0.1;
  }
  &::before {
    top: 0;
  }
  &::after {
    bottom: 0;
  }
  color: var(--theme-text-against-dark);
  padding: calc(2 * var(--text-size)) 0;
  //margin-top: calc(2.5 * var(--text-size));
  //margin-bottom: calc(2.5 * var(--text-size));
  > figure {
    position: relative;
    z-index: 0;
    margin: calc(3 * var(--text-size)) calc(-1 * var(--gutter));
    overflow: hidden;
    height: 75vh;
    position: relative;
    background-position: center center;
    background-size: cover;
    background-repeat: no-repeat;
    width: 100vw;
    left: 50%;
    margin-left: -50vw;
    background-color: var(--theme-background-light);
    &:first-of-type {
      margin-top: calc(-2 * var(--text-size));
    }
    &:last-of-type {
      margin-bottom: calc(-2 * var(--text-size));
    }
    /*
    @supports not (-webkit-overflow-scrolling: touch) {
      background-attachment: fixed;
      &::before,
      &::after {
        content: '';
        position: absolute;
        z-index: 2;
        display: block;
        right: 0.5em;
        left: 0.5em;
        height: 1em;
        border-radius: 50%;
      }
      &::before {
        bottom: 100%;
        box-shadow: 0 0.375em 0.5em rgba(0,0,0,0.1);
      }
      &::after {
        top: 100%;
        box-shadow: 0 -0.375em 0.5em rgba(0,0,0,0.1);
      }
    }
    */
  }
  /*@supports (-webkit-overflow-scrolling: touch) {*/
    z-index: -1;
    overflow: hidden;
    padding: 0 var(--gutter);
    width: 100vw;
    left: 50%;
    margin-left: -50vw;
    figure {
      position: fixed;
      z-index: -1;
      //top: calc((98 / 16) * 1em);
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      margin: 0;
      //height: calc(100vh - ((98 / 16) * 1em));
      height: 100vh;
      background-size: auto calc(100% - 9em);
      @media ( min-width: 60em ) {
        background-size: cover;
      }
      @media ( max-width: 60em ) {
        &::before {
          content: '';
          position: absolute;
          right: 0;
          bottom: 0;
          left: 0;
          height: 5em;
          background-color: var(--theme-background-dark);
        }
      }
    }
    > div {
      position: relative;
      z-index: 2;
      padding: calc(2 * var(--text-size)) 0;
      margin: 75vh 0;
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 50%;
        margin-left: -50vw;
        width: 100vw;
        z-index: -1;
        background-color: var(--theme-background-dark);
      }
      > div {
        max-width: var(--text-max-width);
        margin-right: auto;
        margin-left: auto;
      }
    }
    figure {
     visibility: hidden;
    }
    &.step-1 figure:nth-of-type(1),
    &.step-2 figure:nth-of-type(2),
    &.step-3 figure:nth-of-type(3),
    &.step-4 figure:nth-of-type(4) {
      visibility: visible;
    }
  /*}*/
`
